import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/http';
import {
  writeAddendum,
  type Addendum,
  type InterviewOut,
  type NewAddendum,
} from '@/api/interviews';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { ADDENDUM_MAX_TEXT } from './addendumFields';

interface Props {
  /** The **author's own** submitted interview — `Submitted` renders this control for nobody else. */
  interview: InterviewOut;
  viewerId: number;
}

/**
 * §6.7's `[Add addendum]` — **the only way to change one's mind about submitted feedback** (ADR 0008,
 * protection #5), and the mechanism ADR 0008 has pointed at since Sprint 5 with no interface offering it.
 *
 * ⚠️ **Author only, hidden rather than disabled.** The API answers `403` to everybody else
 * (`Route("post", f"{INTERVIEW}addenda/", …, (401, 404, 403, 403, 403, 403))` in
 * `backend/tests/test_permission_matrix.py`) and §6.7 states the reason: *"a Recruiter's or an Admin's
 * addendum would be an annotation of another person's opinion inside the record of that opinion"* — the
 * corruption ADR 0008 exists to prevent, arriving from a different direction. §5's rule for a capability a
 * group does not have is *hidden, never disabled*; the caller is decided in `Submitted` and the matrix row
 * is the evidence, never this component.
 *
 * ⚠️ **`text` and nothing else.** `AddendumIn` carries one field, and no *"reason"* taxonomy, rating,
 * verdict or date is invented here: ADR 0008's *"a separate entry with a date and a reason"* is
 * `created_at` and the free text. A structured reason would turn a change of mind into a category somebody
 * later filters on.
 *
 * ⚠️ **No derived value is invalidated, and that is a decision.** An addendum carries no rating and no
 * recommendation, so `average_score`, `latest_recommendation`, `interview_count` and
 * `submitted_interview_count` do not move by a digit (`backend/CONTEXT.md`'s *Derived values* row,
 * asserted server-side by TASK-038). Invalidating `person`, `considerations` or `dashboard` here would be
 * harmless and would **state the opposite of what the record means**: ADR 0008's scenario is a 2 that
 * stays a 2 with a paragraph beside it. Only this interview's own entry is updated, from the response.
 */
export function AddAddendum({ interview, viewerId }: Props) {
  const { t } = useTranslation('interviews');
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        {t('addendum.action')}
      </Button>

      {/* Mounted only while open, so a cancelled dialog leaves no half-written text behind. */}
      {open && (
        <AddendumDialog interview={interview} viewerId={viewerId} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function AddendumDialog({
  interview,
  viewerId,
  onClose,
}: Props & {
  onClose: () => void;
}) {
  const { t } = useTranslation(['interviews', 'common', 'components']);
  const queryClient = useQueryClient();

  const [text, setText] = useState('');
  const [tooShort, setTooShort] = useState(false);
  const fieldId = useId();

  const write = useMutation<Addendum, ApiError, NewAddendum>({
    mutationFn: (addendum) => writeAddendum(interview.id, addendum),
    onSuccess: (created) => {
      /**
       * ⚠️ **Appended from the response, and nothing else on the screen moves.** The write touches no
       * column of the interview — `updated_at` included — so the *"Submitted …"* line above is still
       * true, and a refetch would only risk replacing a correct page with a newer one for no reason.
       * `AddendumOut` is exactly the shape the list renders, so the entry that appears is the entry the
       * server stored.
       */
      queryClient.setQueryData<InterviewOut>(queryKeys.interview(viewerId, interview.id), (held) =>
        held === undefined ? held : { ...held, addenda: [...held.addenda, created] },
      );
      onClose();
    },
  });

  /**
   * The server's own rule, kept before the request rather than discovered through a 422: **whitespace is
   * not a comment** (`AddendumIn`'s field is required and the backend strips). The bound is the other
   * half and is **derived from the document** rather than restated — see `ADDENDUM_MAX_TEXT`.
   */
  const submit = () => {
    if (text.trim() === '') {
      setTooShort(true);
      return;
    }

    write.mutate({ text });
  };

  const failure = (() => {
    if (!write.error) return null;
    if (write.error.isOffline) return t('components:state.offlineWrite');
    if (write.error.isThrottled) return t('components:state.throttled');
    /*
     * ⚠️ **The 409 that is reachable here is the anonymised one** (ADR 0009): the erasure closes the
     * record to new activity, and no addendum can be written about that candidate by anybody. The other
     * 409 of this route — *the interview is still a draft* — cannot be reached from this screen at all,
     * because the control exists only on a submitted interview; it is answered in one sentence anyway,
     * because "unreachable from the interface" is not the same as "impossible" (a second tab, a stale
     * page, a payload from before a deploy).
     */
    if (write.error.status === 409) return t('interviews:addendum.refused');
    return t('interviews:addendum.failed');
  })();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('interviews:addendum.title')}
      description={t('interviews:addendum.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <Button isLoading={write.isPending} onClick={submit}>
            {t('interviews:addendum.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2">
        <label htmlFor={fieldId} className="text-xs font-medium text-ink-700">
          {t('interviews:addendum.label')}
        </label>
        <Textarea
          id={fieldId}
          value={text}
          maxLength={ADDENDUM_MAX_TEXT}
          aria-invalid={tooShort || undefined}
          aria-describedby={tooShort ? `${fieldId}-empty` : undefined}
          onChange={(event) => {
            setText(event.target.value);
            if (tooShort) setTooShort(false);
          }}
        />

        {tooShort && (
          <p id={`${fieldId}-empty`} className={cn(type.metadata, 'text-negative')}>
            {t('interviews:addendum.empty')}
          </p>
        )}

        {failure && (
          <p role="alert" className={cn(type.body, 'text-negative')}>
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
